import { notify } from 'Components/common/notify';

import fetch from 'Utils/fetchWrapper';
import { error } from 'Utils/logUtils';
import { getCurrentLanguageCode } from 'Utils/stateUtils';
import { addQueryParamsToUrl, getApiCDNBaseUrlV2 } from 'Utils/urlUtils';

import { decrementAPICount } from 'Actions/apiCount';
import {
	appendReviewsByCity,
	appendReviewsByCollection,
	receiveReviewsByCity,
	receiveReviewsByCityAndCategory,
	receiveReviewsByCityAndSubCategory,
	receiveReviewsByCollection,
	receiveReviewsByPersona,
	receiveReviewsByTgid,
	requestReviewsByCity,
	requestReviewsByCityAndCategory,
	requestReviewsByCityAndSubCategory,
	requestReviewsByCollection,
	requestReviewsByPersona,
	requestReviewsByTgid,
} from 'Actions/review';
import { setAPIServerAPIStatus } from 'Actions/serverStatus';
import type {
	TReviewCityCategoryParam,
	TReviewCitySubCategoryParam,
	TReviewThunkCityParam,
	TReviewThunkCollectionParam,
	TReviewThunkPersonaParam,
	TReviewThunkTGIDParam,
} from 'ReduxTypes/review';

export const fetchReviewsByTgid =
	({
		tgId,
		lang = 'en',
		limit = 20,
		filterType = 'TOP',
	}: TReviewThunkTGIDParam) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const url = `${getApiCDNBaseUrlV2({
			state,
		})}/api/v2/review/tour-group/id/${tgId}?language=${lang.toUpperCase()}&limit=${limit}`;

		dispatch(requestReviewsByTgid({ tgId, filterType }));

		return fetch(url)
			.then(response => response.json())
			.then(json =>
				dispatch(
					receiveReviewsByTgid({ tgId, reviews: json, filterType }),
				),
			)
			.catch(err => {
				dispatch(setAPIServerAPIStatus(url, err.status));
				error(err);
				notify.showNetworkError(err);
				dispatch(decrementAPICount());
			});
	};

export const fetchReviewsByCollection =
	({
		collectionId,
		secondaryCity,
		limit = 8,
		offset = 0,
	}: Omit<TReviewThunkCollectionParam, 'lang'>) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const lang = getCurrentLanguageCode(state);
		const apiCDNBaseUrl = getApiCDNBaseUrlV2({
			state,
		});
		const urlBase = `${apiCDNBaseUrl}/api/v2/collections/${collectionId}/reviews`;
		const url = addQueryParamsToUrl(urlBase, {
			limit,
			offset,
			language: lang.toUpperCase(),
			...(secondaryCity && {
				'secondary-city': secondaryCity,
			}),
		})!;

		dispatch(requestReviewsByCollection({ collectionId }));

		return fetch(url)
			.then(response => response.json())
			.then(json => {
				if (offset === 0) {
					dispatch(
						receiveReviewsByCollection({
							collectionId,
							reviewsData: json,
						}),
					);
				} else {
					dispatch(
						appendReviewsByCollection({
							collectionId,
							reviewsData: json,
						}),
					);
				}
			})
			.catch(err => {
				error(err);
				notify.showNetworkError(err);
				dispatch(decrementAPICount());
			});
	};

export const fetchReviewsByCity =
	({ city, limit = 8, offset = 0 }: Omit<TReviewThunkCityParam, 'lang'>) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const lang = getCurrentLanguageCode(state);
		const apiCDNBaseUrl = getApiCDNBaseUrlV2({
			state,
		});
		const urlBase = `${apiCDNBaseUrl}/api/v3/cities/${city}/reviews`;
		const url = addQueryParamsToUrl(urlBase, {
			limit,
			offset,
			language: lang.toUpperCase(),
		})!;

		dispatch(requestReviewsByCity({ city }));

		return fetch(url)
			.then(response => response.json())
			.then(json => {
				if (offset === 0) {
					dispatch(
						receiveReviewsByCity({
							city,
							reviewsData: json,
						}),
					);
				} else {
					dispatch(appendReviewsByCity({ city, reviewsData: json }));
				}
			})
			.catch(err => {
				error(err);
				notify.showNetworkError(err);
				dispatch(decrementAPICount());
			});
	};

export const fetchReviewsByPersona =
	({
		personaAffinityId,
		city,
		limit = 8,
		offset = 0,
	}: Omit<TReviewThunkPersonaParam, 'lang'>) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const lang = getCurrentLanguageCode(state);
		const apiCDNBaseUrl = getApiCDNBaseUrlV2({
			state,
		});
		const urlBase = `${apiCDNBaseUrl}/api/v2/persona-affinities/${personaAffinityId}/reviews/`;
		const url = addQueryParamsToUrl(urlBase, {
			city,
			limit,
			offset,
			language: lang.toUpperCase(),
		})!;

		dispatch(requestReviewsByPersona({ city, personaAffinityId }));

		return fetch(url)
			.then(response => response.json())
			.then(json => {
				dispatch(
					receiveReviewsByPersona({
						personaAffinityId,
						city,
						reviewsData: json,
					}),
				);
			})
			.catch(err => {
				error(err);
				notify.showNetworkError(err);
				dispatch(decrementAPICount());
			});
	};

export const fetchReviewsByCityAndCategory =
	({
		categoryId,
		city,
		limit = 8,
		offset = 0,
	}: Omit<TReviewCityCategoryParam, 'lang' | 'reviews'>) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const lang = getCurrentLanguageCode(state);
		const apiCDNBaseUrl = getApiCDNBaseUrlV2({
			state,
		});
		const urlBase = `${apiCDNBaseUrl}/api/v3/cities/${city}/categories/${categoryId}/reviews/`;
		const url = addQueryParamsToUrl(urlBase, {
			limit,
			offset,
			language: lang.toUpperCase(),
		})!;

		dispatch(requestReviewsByCityAndCategory({ city, categoryId }));

		return fetch(url)
			.then(response => response.json())
			.then(json => {
				dispatch(
					receiveReviewsByCityAndCategory({
						city,
						categoryId,
						reviewsData: json,
					}),
				);
			})
			.catch(err => {
				error(err);
				notify.showNetworkError(err);
			});
	};

export const fetchReviewsByCityAndSubCategory =
	({
		subCategoryId,
		city,
		limit = 8,
		offset = 0,
	}: Omit<TReviewCitySubCategoryParam, 'lang'>) =>
	(dispatch: any, getState: any) => {
		const state = getState();
		const lang = getCurrentLanguageCode(state);
		const apiCDNBaseUrl = getApiCDNBaseUrlV2({
			state,
		});
		const urlBase = `${apiCDNBaseUrl}/api/v3/cities/${city}/subcategories/${subCategoryId}/reviews/`;
		const url = addQueryParamsToUrl(urlBase, {
			limit,
			offset,
			language: lang.toUpperCase(),
		})!;

		dispatch(requestReviewsByCityAndSubCategory({ city, subCategoryId }));

		return fetch(url)
			.then(response => response.json())
			.then(json => {
				dispatch(
					receiveReviewsByCityAndSubCategory({
						city,
						subCategoryId,
						reviewsData: json,
					}),
				);
			})
			.catch(err => {
				error(err);
				notify.showNetworkError(err);
			});
	};
